// CheckMaty SearchProvider — wires the @plasser/ui EntityPicker to Supabase.
// ---------------------------------------------------------------------------
// RLS is the security boundary (CLAUDE.md): every read is scoped by the table's
// RLS SELECT policy for the current user-JWT; every write is gated by the table's
// RLS INSERT policy. The picker UI is convenience, never access control.
//
// SEARCH (A1a landed — TES-2037): `search()` calls the server-side RPC
// `search_entities(entity_type, q, filters, limit, offset)` (migration
// 405_search_entities.sql). The RPC is SECURITY INVOKER, so the AND-token search
// + facet filters run IN SQL over the FULL RLS-scoped set for the caller — the
// earlier `FETCH_CAP = 1000` client-side window is GONE (rows beyond it used to be
// unfindable in profiles/machines pickers). The RPC returns the display-ready
// record shape (id, name, subtitle, attributes) plus a full `total_count` for the
// picker's infinite-scroll `hasMore`. QuickCreate (`create`) still writes directly,
// gated by the table's RLS INSERT policy.

import type {
  EntityRecord,
  EntityTypeConfig,
  SearchProvider,
  SearchQuery,
  SearchResult,
} from "@plasser/ui";

import { supabase } from "@/integrations/supabase/client";

interface TableDescriptor {
  table: string;
  /** Explicit columns to select on QuickCreate insert-return (never '*'). */
  columns: string[];
  /** Builds an EntityRecord from a raw insert-return row. */
  toRecord: (row: Record<string, unknown>) => EntityRecord;
  /** QuickCreate: builds the INSERT payload (RLS still enforces the create right). */
  toInsert?: (values: Record<string, string>, ctx: ProviderContext) => Record<string, unknown>;
  /** UI create-right gate (NOT security): permission entity+action to probe. */
  createGate?: { entity: string; action: string; level?: string };
}

export interface ProviderContext {
  /** Current principal (auth.uid). Used for the canCreate probe + insert defaults. */
  userId?: string | null;
  /** Active team id (for team-scoped inserts, e.g. team_group). */
  activeTeamId?: string | null;
}

const s = (v: unknown): string | null => (v == null ? null : String(v));

// QuickCreate descriptors. Only entity types that are creatable from a picker
// need one; `search` no longer uses these (the RPC returns the record shape).
// Groups (team_group ⊂ teams) — INSERT gated on teams:manage (tester denied, see pgTAP).
const DESCRIPTORS: Record<string, TableDescriptor> = {
  group: {
    table: "team_group",
    columns: ["id", "name", "description", "team_id", "is_active", "standort", "group_type"],
    toRecord: (r) => ({
      id: String(r.id),
      name: String(r.name ?? ""),
      subtitle: s(r.description),
      attributes: {
        team: s(r.team_id),
        standort: s(r.standort),
        group_type: s(r.group_type),
      },
    }),
    toInsert: (values, ctx) => ({
      name: values.name,
      description: values.description || null,
      team_id: ctx.activeTeamId,
    }),
    createGate: { entity: "teams", action: "manage", level: "full" },
  },
};

// The `search_entities` RPC is not in the generated client types (it is created by
// migration 405, deployed via GitHub Actions — types.ts is regenerated post-deploy).
// The picker is generic over a runtime entity type, so we access the client through
// a narrow dynamic view. RLS still applies — this is a typing escape, not a
// privilege escape (the RPC is SECURITY INVOKER; INSERT is RLS-gated).
type DbResult<T> = { data: T; error: { message: string } | null };
/** One row of the `search_entities` RPC result set. */
interface SearchRow {
  id: string;
  name: string | null;
  subtitle: string | null;
  attributes: Record<string, string | null> | null;
  /** Full scoped hit count (count(*) OVER ()); identical on every row. */
  total_count: number | string | null;
}
interface DynamicClient {
  from(table: string): {
    insert(payload: Record<string, unknown>): {
      select(cols: string): {
        single(): PromiseLike<DbResult<Record<string, unknown> | null>>;
      };
    };
  };
  rpc(fn: string, args: Record<string, unknown>): PromiseLike<DbResult<SearchRow[] | null>>;
}

export function createSupabaseSearchProvider(ctx: ProviderContext = {}): SearchProvider {
  const db = supabase as unknown as DynamicClient;
  return {
    async search(config: EntityTypeConfig, query: SearchQuery): Promise<SearchResult> {
      // Server-side, RLS-scoped AND-token search over the FULL scoped set (no
      // client-side 1000-row window). The RPC decides which entity types exist
      // (unknown type → SQL error surfaced here). See migration 405.
      const { data, error } = await db.rpc("search_entities", {
        _entity_type: config.type,
        _q: query.text ?? "",
        _filters: query.filters ?? {},
        _limit: query.limit ?? 50,
        _offset: query.offset ?? 0,
      });
      if (error) throw new Error(error.message);

      const rows = data ?? [];
      const items: EntityRecord[] = rows.map((r) => ({
        id: String(r.id),
        name: String(r.name ?? ""),
        subtitle: r.subtitle ?? null,
        attributes: (r.attributes ?? {}) as Record<string, string | null>,
      }));
      // total_count is the full scoped hit count (may exceed items.length when
      // the result is paginated) — drives the picker's hit counter + hasMore.
      const total = rows.length > 0 ? Number(rows[0].total_count ?? rows.length) : 0;
      return { items, total };
    },

    async create(config: EntityTypeConfig, values: Record<string, string>): Promise<EntityRecord> {
      const desc = DESCRIPTORS[config.type];
      if (!desc?.toInsert) {
        throw new Error(`QuickCreate not supported for entity type "${config.type}"`);
      }
      const payload = desc.toInsert(values, ctx);
      // RLS INSERT policy is the authoritative create gate (e.g. teams:manage).
      const { data, error } = await db
        .from(desc.table)
        .insert(payload)
        .select(desc.columns.join(", "))
        .single();
      if (error) throw new Error(error.message);
      return desc.toRecord((data ?? {}) as Record<string, unknown>);
    },

    async canCreate(config: EntityTypeConfig): Promise<boolean> {
      const desc = DESCRIPTORS[config.type];
      if (!desc?.toInsert || !desc.createGate || !ctx.userId) return false;
      // UI gate only (best-effort) — RLS is authoritative on write. Probe the same
      // role predicate the team_group INSERT policy uses for the permission check:
      // has_permission_in_team(teams, manage, full). NOTE (migration 286): this RPC is
      // tenant-decoupled and role-only — it does NOT verify active-team membership; the
      // team scope is enforced by a SEPARATE RLS clause (team_id = ANY(get_user_team_ids))
      // on the INSERT (migration 325). So the affordance reflects the role grant; a user
      // with the role but not in the active team is still rejected by RLS on write.
      const { data, error } = await supabase.rpc("has_permission_in_team", {
        _user_id: ctx.userId,
        _entity: desc.createGate.entity,
        _action: desc.createGate.action,
        _level: desc.createGate.level ?? "full",
      });
      if (error) return false;
      return Boolean(data);
    },
  };
}
